// « Les règles de ce compte ont changé » — proposition, jamais imposition.
//
// Volontairement une BANNIÈRE et non un modal : contrairement au passage en
// funded (une bonne nouvelle qui mérite d'interrompre), un changement de règles
// n'a pas à bloquer l'écran. Le trader la traite quand il veut ; d'ici là ses
// alertes continuent de tourner sur ses règles d'origine, ce qui est l'état sûr.

import { useState } from "react";
import {
  getRuleUpdatePrompts,
  respondToRuleUpdatePrompts,
  useQuery,
  useAction,
} from "wasp/client/operations";
import { Button } from "../../client/components/ui/button";
import { Badge } from "../../client/components/ui/badge";
import { useToast } from "../../client/hooks/use-toast";
import useTranslation from "../../client/hooks/useTranslation";
import { useLanguageContext } from "../../client/contexts/LanguageContext";
import { AlertTriangle, ArrowRight } from "lucide-react";

interface ChangeSummary {
  field: string;
  label: string;
  phaseName?: string;
  before: unknown;
  after: unknown;
  direction: "stricter" | "looser" | "unknown";
}

interface RuleUpdatePrompt {
  accountId: string;
  accountName: string;
  propFirmName: string | null;
  frozenAt: string | null;
  changes: ChangeSummary[];
}

function fmtValue(v: unknown, locale: string): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return new Intl.NumberFormat(locale).format(v);
  return String(v);
}

/**
 * @param accountId  Limite l'affichage à un compte (fiche compte). Omis, la
 *                   bannière couvre tous les comptes concernés.
 */
export default function RuleUpdateBanner({ accountId }: { accountId?: string }) {
  const t = useTranslation();
  const { language } = useLanguageContext();
  const locale = language === "fr" ? "fr-FR" : "en-US";
  const { toast } = useToast();
  const { data, refetch } = useQuery(getRuleUpdatePrompts, {});
  const respond = useAction(respondToRuleUpdatePrompts);

  const [busy, setBusy] = useState<string | null>(null);

  const all = (data as RuleUpdatePrompt[] | undefined) ?? [];
  const prompts = accountId ? all.filter((p) => p.accountId === accountId) : all;

  if (prompts.length === 0) return null;

  const handle = async (id: string, accepted: boolean) => {
    setBusy(id);
    try {
      await respond({ responses: [{ accountId: id, accepted }] });
      toast({
        title: accepted
          ? t.tradingAccounts.ruleUpdateAccepted
          : t.tradingAccounts.ruleUpdateDeclined,
      });
      refetch();
    } catch (e) {
      toast({ title: (e as Error).message, variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-3">
      {prompts.map((prompt) => (
        <div
          key={prompt.accountId}
          className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 space-y-3"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-sm">
                {t.tradingAccounts.ruleUpdateTitle}
                {!accountId && (
                  <span className="text-muted-foreground font-normal">
                    {" "}
                    · {prompt.accountName}
                    {prompt.propFirmName ? ` (${prompt.propFirmName})` : ""}
                  </span>
                )}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {t.tradingAccounts.ruleUpdateBody}
              </p>
              {prompt.frozenAt && (
                <p className="text-xs text-muted-foreground mt-1">
                  {t.tradingAccounts.ruleUpdateFrozenOn.replace(
                    "{date}",
                    new Date(prompt.frozenAt).toLocaleDateString(locale),
                  )}
                </p>
              )}
            </div>
          </div>

          <ul className="space-y-1 pl-8">
            {prompt.changes.map((c, i) => (
              <li key={i} className="text-sm flex flex-wrap items-center gap-2">
                <span className="text-muted-foreground">
                  {c.phaseName ? `${c.phaseName} · ` : ""}
                  {c.label}
                </span>
                <span className="font-mono text-xs">{fmtValue(c.before, locale)}</span>
                <ArrowRight className="w-3 h-3 text-muted-foreground" />
                <span className="font-mono text-xs font-medium">{fmtValue(c.after, locale)}</span>
                {c.direction !== "unknown" && (
                  <Badge
                    variant={c.direction === "stricter" ? "destructive" : "secondary"}
                    className="text-[10px]"
                  >
                    {c.direction === "stricter"
                      ? t.tradingAccounts.ruleUpdateStricter
                      : t.tradingAccounts.ruleUpdateLooser}
                  </Badge>
                )}
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap gap-2 pl-8">
            <Button
              size="sm"
              onClick={() => handle(prompt.accountId, true)}
              disabled={busy === prompt.accountId}
            >
              {t.tradingAccounts.ruleUpdateAccept}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handle(prompt.accountId, false)}
              disabled={busy === prompt.accountId}
            >
              {t.tradingAccounts.ruleUpdateDecline}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
